import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Header from '../../components/navigation/Header';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | ready | submitting | success | error | invalid
  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // Supabase sends the recovery token via URL hash fragment (#access_token=...&type=recovery)
    // onAuthStateChange fires a PASSWORD_RECOVERY event when the token is valid
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setStatus('ready');
      } else if (event === 'SIGNED_IN' && status === 'loading') {
        // Already authenticated, shouldn't be here
        setStatus('ready');
      }
    });

    // Also check if there's a hash with access_token (direct link visit)
    const hash = window.location?.hash;
    if (hash && hash?.includes('access_token') && hash?.includes('type=recovery')) {
      // Supabase will handle this via onAuthStateChange above
      // Set a timeout fallback in case the event fires before listener is attached
      setTimeout(() => {
        setStatus(prev => prev === 'loading' ? 'ready' : prev);
      }, 1500);
    } else if (!hash || !hash?.includes('access_token')) {
      // No token in URL — invalid access
      setStatus('invalid');
    }

    return () => subscription?.unsubscribe();
  }, []);

  const validate = () => {
    const newErrors = {};
    if (!formData?.password) newErrors.password = 'Password is required';
    else if (formData?.password?.length < 6) newErrors.password = 'Password must be at least 6 characters';
    if (!formData?.confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
    else if (formData?.password !== formData?.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    setErrorMessage('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors)?.length > 0) { setErrors(validationErrors); return; }

    setStatus('submitting');
    setErrorMessage('');

    const { error } = await supabase?.auth?.updateUser({ password: formData?.password });

    if (error) {
      setStatus('ready');
      setErrorMessage(error?.message || 'Failed to reset password. The link may have expired.');
    } else {
      setStatus('success');
      setTimeout(() => navigate('/user-dashboard', { replace: true }), 2500);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <Header
        user={null}
        onNavigate={(path) => navigate(path)}
        onPurchaseCredits={() => {}}
        onLogout={() => {}}
      />
      <div className="flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-600 to-blue-500 shadow-lg mb-4">
            <Icon name="Camera" size={28} color="white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Angle</h1>
          <p className="text-gray-500 text-sm mt-1">Find your best angle with AI</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          {status === 'loading' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-5">
                <Icon name="Loader2" size={32} color="#3b82f6" className="animate-spin" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Verifying reset link…</h2>
              <p className="text-gray-500 text-sm">Please wait while we validate your reset token.</p>
            </motion.div>
          )}

          {(status === 'ready' || status === 'submitting') && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-purple-100 mb-4">
                  <Icon name="LockKeyhole" size={28} color="#7c3aed" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">Set new password</h2>
                <p className="text-sm text-gray-500">Choose a strong password for your account.</p>
              </div>

              {errorMessage && (
                <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg mb-5">
                  <Icon name="AlertCircle" size={16} color="#ef4444" className="mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-600">{errorMessage}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">New Password</label>
                  <input
                    type="password"
                    name="password"
                    value={formData?.password}
                    onChange={handleChange}
                    placeholder="At least 6 characters"
                    className={`w-full px-4 py-3 rounded-xl border text-sm transition-colors outline-none focus:ring-2 focus:ring-purple-500/20 ${
                      errors?.password ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-purple-400'
                    }`}
                  />
                  {errors?.password && <p className="text-xs text-red-500 mt-1">{errors?.password}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm New Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData?.confirmPassword}
                    onChange={handleChange}
                    placeholder="Re-enter your new password"
                    className={`w-full px-4 py-3 rounded-xl border text-sm transition-colors outline-none focus:ring-2 focus:ring-purple-500/20 ${
                      errors?.confirmPassword ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-purple-400'
                    }`}
                  />
                  {errors?.confirmPassword && <p className="text-xs text-red-500 mt-1">{errors?.confirmPassword}</p>}
                </div>

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-blue-500 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-600 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md"
                >
                  {status === 'submitting' ? (
                    <><Icon name="Loader2" size={18} className="animate-spin" /> Updating password...</>
                  ) : (
                    <><Icon name="ShieldCheck" size={18} /> Reset Password</>
                  )}
                </button>
              </form>
            </motion.div>
          )}

          {status === 'success' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-5">
                <Icon name="CheckCircle" size={32} color="#16a34a" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Password updated!</h2>
              <p className="text-gray-500 text-sm">Your password has been reset successfully. Redirecting you to your dashboard…</p>
            </motion.div>
          )}

          {status === 'invalid' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-5">
                <Icon name="XCircle" size={32} color="#dc2626" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Invalid or expired link</h2>
              <p className="text-gray-500 text-sm mb-6">
                This password reset link is invalid or has expired. Please request a new one.
              </p>
              <button
                onClick={() => navigate('/forgot-password')}
                className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-blue-500 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-600 transition-all shadow-md text-sm mb-3"
              >
                Request New Link
              </button>
              <button
                onClick={() => navigate('/auth')}
                className="w-full py-3 px-4 border border-gray-200 text-gray-600 font-medium rounded-xl hover:bg-gray-50 transition-colors text-sm"
              >
                Back to Sign In
              </button>
            </motion.div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
